import { useMemo } from 'react';

import {
  OidcGroupMappingDto,
  OidcGroupMappingItemDto,
  useOidcGroupMappingListControllerListQuery,
  useOidcGroupMappingsReplaceControllerReplaceMutation,
} from '@shared/services/apiService';

const toItem = ({ connectorId, groupValue, permissionGroup }: OidcGroupMappingDto) => ({
  connectorId,
  groupValue,
  permissionGroupId: permissionGroup.id,
});

/**
 * SSO group mappings of one company — platform admin only. The API replaces the company's whole
 * set at once, so every change here sends the full list with that one row added, edited or removed.
 */
export const useSsoMappingsApi = (companyId?: string) => {
  const list = useOidcGroupMappingListControllerListQuery(
    { companyId },
    { skip: companyId === undefined },
  );
  const [replace] = useOidcGroupMappingsReplaceControllerReplaceMutation();

  const mappings = useMemo(() => list.data?.mappings ?? [], [list.data]);

  const actions = useMemo(() => {
    const save = (items: OidcGroupMappingItemDto[]) =>
      replace({
        id: companyId ?? '',
        oidcGroupMappingsReplaceRequestDto: { mappings: items },
      }).unwrap();

    return {
      /** `replacing` is the id of the row being edited; without it the item is added. */
      saveMapping: (item: OidcGroupMappingItemDto, replacing?: string) =>
        save(
          replacing
            ? mappings.map((mapping) => (mapping.id === replacing ? item : toItem(mapping)))
            : [...mappings.map(toItem), item],
        ),
      removeMapping: (mappingId: string) =>
        save(mappings.filter((mapping) => mapping.id !== mappingId).map(toItem)),
    };
  }, [replace, companyId, mappings]);

  return {
    connectorIds: list.data?.connectorIds ?? [],
    mappings,
    isLoading: list.isLoading,
    /** Federated sign-in is off in this environment: nothing could be mapped. */
    isSsoDisabled: list.data?.connectorIds.length === 0,
    ...actions,
  };
};

export type SsoMappingsApi = ReturnType<typeof useSsoMappingsApi>;
